import React, { useEffect, useMemo, useState } from 'react';
import { resolveImageUrl } from '../../utils/imageUrl';
import { trackEvent } from '../../lib/analytics';
import { useLanguage } from '../../contexts/LanguageContext';
import { staffGenderSchema, STAFF_GENDER_MALE, STAFF_GENDER_FEMALE } from '../../schemas/staff.schema';
import StaffAbsencesSection from './StaffAbsencesSection';
import styles from './ManageStaffModal.module.css';

/**
 * CR-2026-05-17 / AB#1713 — sentinel key for services with no
 * `categoryName`. Uses a Unicode character that's invalid in a real
 * category name so it can't collide with a category literally called
 * "Other".
 */
const OTHER_GROUP_KEY = ' __other__';
const OTHER_GROUP_LABEL = 'Other';

/**
 * CR-2026-05-17 — Render-time grouping of `StaffServiceOption[]` by
 * `categoryName`. Services with no category fall into the *Other* bucket
 * (always rendered last regardless of writing direction).
 */
interface ServiceGroup {
    key: string;            // categoryName or OTHER_GROUP_KEY
    label: string;          // displayed name
    services: StaffServiceOption[];
}

function groupServicesByCategory(services: readonly StaffServiceOption[]): ServiceGroup[] {
    const buckets: Record<string, StaffServiceOption[]> = {};
    for (const svc of services) {
        const key = svc.categoryName?.trim() || OTHER_GROUP_KEY;
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(svc);
    }
    const groups: ServiceGroup[] = Object.keys(buckets)
        .filter(key => key !== OTHER_GROUP_KEY)
        .map(key => ({ key, label: key, services: sortServices(buckets[key]) }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    const other = buckets[OTHER_GROUP_KEY];
    if (other && other.length > 0) {
        groups.push({ key: OTHER_GROUP_KEY, label: OTHER_GROUP_LABEL, services: sortServices(other) });
    }
    return groups;
}

function sortServices(list: StaffServiceOption[]): StaffServiceOption[] {
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
}

/** Fill `{name}` placeholders in a translated template. */
function fill(template: string, values: Record<string, string | number>): string {
    return Object.entries(values).reduce(
        (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
        template,
    );
}

/** Case-insensitive substring match across service name + category name. */
function serviceMatchesQuery(svc: StaffServiceOption, q: string): boolean {
    if (!q) return true;
    return (
        svc.name.toLowerCase().includes(q) ||
        (svc.categoryName?.toLowerCase().includes(q) ?? false)
    );
}

/** Convert a name into a 1–2 letter monogram for the avatar fallback. */
function getInitials(fullName: string): string {
    const trimmed = (fullName ?? '').trim();
    if (!trimmed) return '?';
    return trimmed
        .split(/\s+/)
        .map(part => part[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

/** Resolve a staff photo URL — may be relative (uploaded via /uploads/...) or an absolute provider URL. */
function resolveStaffPhoto(url: string | null | undefined): string {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return resolveImageUrl(url);
}

/**
 * CR-2026-05-15-v4 — Manage Staff add/edit modal (canonical My Business pattern).
 *
 * Mirrors ServiceAddEditModal + BranchAddEditModal: right-slide-in panel,
 * dark Fresha tokens, sectioned form, sticky footer. This is the future
 * home of staff management — replaces the legacy /employee page's modal.
 *
 * Form fields:
 *   - Full Name (required)
 *   - Email (required, format-validated)
 *   - Phone Number (required)
 *   - Gender (Male / Female chip select)
 *   - Position (free text)
 *   - Role (Staff / Staff Manager — visible only when caller has canManageStaff)
 *   - Active toggle
 *   - "Services this staff can perform" capability chip multi-select
 *   - Schedule view (read-only on edit)
 *
 * The capability set is what makes /my-business/services answer "who can
 * do this?". See CR-2026-05-15-v3 for the BE endpoint that backs this.
 */

export interface StaffServiceOption {
    id: number;
    name: string;
    isActive?: boolean;
    categoryName?: string;
    hasHomeService?: boolean;
    /** AB#1734 follow-up — branch this service belongs to. Used to scope the
     *  capability picker so only services from the staff's enrolled branches
     *  are selectable. */
    branchId?: number;
}

/** CR-2026-05-16-v2 — minimal branch shape for the "Works at branches" chip selector. */
export interface StaffBranchOption {
    id: number;
    name: string;
    isPrimary?: boolean;
}

export interface StaffScheduleEntry {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
}

export interface StaffFormData {
    id?: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    /**
     * AB#1845 (PBI-E) — gender is REQUIRED at invite time. `null` means
     * "owner hasn't picked yet" (new staff) or "legacy row missing the
     * field" (existing staff before this PBI shipped). The modal blocks
     * save until a non-null Male (1) or Female (2) is selected. The BE
     * mirrors this with `STAFF_GENDER_REQUIRED`.
     */
    gender: number | null;
    position: string;
    role: number;   // 1 = Staff Manager, 2 = Staff
    isActive: boolean;
    schedules?: StaffScheduleEntry[];
    /** CR-2026-05-16-v1 — current staff photo URL (resolved or relative). */
    photoUrl?: string | null;
    /** Batch B (AB#2176) — a portal login is linked to this staff record. */
    hasLogin?: boolean;
    /** Batch B (AB#2176) — a portal invitation for this record is outstanding. */
    invitationPending?: boolean;
}

export interface ManageStaffModalProps {
    isOpen: boolean;
    isAdd: boolean;
    initial: StaffFormData | null;
    services: StaffServiceOption[];
    initialServiceIds: number[];
    /** CR-2026-05-16-v2 — branches available to enroll the staff in. */
    branches?: StaffBranchOption[];
    initialBranchIds?: number[];
    // AB#1734 follow-up — initialScheduleSlots prop removed. Schedule editing
    // moved to the dedicated Schedule tab (page-level).
    loading?: boolean;
    /**
     * CR-2026-05-16-v1 — invoked when the owner picks a photo. Parent uploads
     * via `apiService.setStaffPhoto` and returns the new URL so the modal can
     * refresh the preview without a round trip through the parent's staff
     * list. Only available in edit mode (we need a staffId to attach to).
     */
    onUploadPhoto?: (file: File) => Promise<string>;
    /**
     * AB#1845 (PBI-E) / AB#1842 (PBI-F) — When the BE rejects an invitation
     * with a field-level code (`STAFF_GENDER_REQUIRED` or
     * `STAFF_GENDER_MISMATCHES_SALON`), the parent sets this prop and the
     * modal renders the localized message under the matching field. The
     * modal does NOT clear this — the parent owns the lifecycle so it
     * survives a re-render while still being dismissable by editing the
     * offending field (the modal clears its own `errors[field]` entry on
     * next `updateField` of that field).
     */
    externalError?: {
        field: 'gender';
        code: 'STAFF_GENDER_REQUIRED' | 'STAFF_GENDER_MISMATCHES_SALON';
    } | null;
    /**
     * AB#1842 (PBI-F) — The host business's `ServiceType`. When set to a
     * restrictive value (`MenOnly` / `WomenOnly`), the modal:
     *   1. Pre-selects the compatible gender chip on first open (Add mode).
     *   2. Greys out + disables the incompatible chip with a tooltip.
     *   3. Blocks save with the localized `STAFF_GENDER_MISMATCHES_SALON`
     *      error when an inconsistent value somehow reaches the submit.
     *
     * `Everyone` (the default) keeps both chips selectable. `null` / `undefined`
     * is treated as `Everyone` for back-compat — the parent must opt-in by
     * passing the business's actual ServiceType.
     *
     * Accepts the raw BE shape (string enum name OR numeric value) so the
     * caller doesn't need to normalise. The modal's lookup covers both —
     * unknown strings / numbers fall back to `Everyone`. The widened
     * `string | number` overload is what `BusinessDetailsResponse.serviceType`
     * actually surfaces today (see types/api-responses.ts line 510).
     */
    salonServiceType?: number | string | null;
    onClose: () => void;
    /**
     * AB#1734 follow-up — onSave signature trimmed back. The modal now
     * handles identity (form fields) + capabilities (services, branches);
     * the parent fires `setStaffServices` and `setStaffBranches` after
     * the main save. Schedule lives in ScheduleTab now.
     */
    onSave: (
        form: StaffFormData,
        serviceIds: number[],
        branchIds: number[],
    ) => void;
    onDelete?: () => void;
}

const DEFAULT_FORM: StaffFormData = {
    fullName: '',
    email: '',
    phoneNumber: '',
    // AB#1845 (PBI-E) — gender starts unselected. Owner must explicitly
    // pick Male or Female before the modal lets them save.
    gender: null,
    position: '',
    role: 2,
    isActive: true,
};

// AB#1734 follow-up — schedule type definitions + helpers moved to
// ScheduleTab. The modal no longer renders schedule controls.

/**
 * AB#1842 (PBI-F) — Normalise the host business's `ServiceType` into one of
 * three sentinel strings, regardless of whether the BE handed us the enum
 * name (`"WomenOnly"`) or the numeric value (`2`). `null` / `undefined` /
 * unknown values fall back to `"Everyone"` so existing call sites that
 * don't yet pass the prop keep their current (permissive) behavior.
 */
function normaliseSalonServiceType(
    raw: number | string | null | undefined,
): 'MenOnly' | 'WomenOnly' | 'Everyone' {
    if (raw === 1 || raw === 'MenOnly') return 'MenOnly';
    if (raw === 2 || raw === 'WomenOnly') return 'WomenOnly';
    return 'Everyone';
}

const ManageStaffModal: React.FC<ManageStaffModalProps> = ({
    isOpen,
    isAdd,
    initial,
    services,
    initialServiceIds,
    branches = [],
    initialBranchIds = [],
    loading = false,
    onUploadPhoto,
    externalError,
    salonServiceType,
    onClose,
    onSave,
    onDelete,
}) => {
    const { t } = useLanguage();
    // Batch B (AB#2176) — contact details are optional (portal invitation) when adding a
    // staff record, or when editing a record that has no login and no pending invitation.
    const contactOptional = isAdd || (!initial?.hasLogin && !initial?.invitationPending);
    // A record without a login (invitation pending or not) has no contact details on
    // file, so they are never required to save it.
    const contactNotRequired = isAdd || initial?.hasLogin === false;
    // AB#1842 (PBI-F) — derive once. Used for chip pre-select, disabled
    // state, tooltip copy, and the save-time guard rail. `requiredGender`
    // is the only gender the modal will let the owner save when the
    // salon is restrictive; `forbiddenGender` is the one whose chip is
    // greyed out with the constraint tooltip.
    const normalisedSalonType = normaliseSalonServiceType(salonServiceType);
    const requiredGender: number | null =
        normalisedSalonType === 'WomenOnly' ? STAFF_GENDER_FEMALE
        : normalisedSalonType === 'MenOnly' ? STAFF_GENDER_MALE
        : null;
    const forbiddenGender: number | null =
        normalisedSalonType === 'WomenOnly' ? STAFF_GENDER_MALE
        : normalisedSalonType === 'MenOnly' ? STAFF_GENDER_FEMALE
        : null;
    const genderTooltip: string | null =
        normalisedSalonType === 'WomenOnly' ? t('staff.invite.genderTooltip.womenOnly')
        : normalisedSalonType === 'MenOnly' ? t('staff.invite.genderTooltip.menOnly')
        : null;

    const [form, setForm] = useState<StaffFormData>(initial ?? DEFAULT_FORM);
    const [selectedServiceIds, setSelectedServiceIds] = useState<Set<number>>(
        () => new Set(initialServiceIds),
    );
    // CR-2026-05-16-v2 — branch enrollment state. Mirrors selectedServiceIds.
    const [selectedBranchIds, setSelectedBranchIds] = useState<Set<number>>(
        () => new Set(initialBranchIds),
    );
    const [serviceQuery, setServiceQuery] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});

    // CR-2026-05-17 / AB#1713 — per-group expand state for the services
    // chip section. Set keys are `ServiceGroup.key` (categoryName or
    // OTHER_GROUP_KEY). Default state is computed by the initialization
    // effect below: a group is expanded by default when at least one of
    // its services is in `initialServiceIds`. While a search query is
    // active the renderer auto-expands matching groups regardless of
    // this state — see `groupRenderStates` below.
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
    // CR-2026-05-16-v1 — local preview URL while the upload is in flight, so
    // the photo flips from "uploading" to "saved" without waiting for a
    // parent re-render. Cleared when the modal closes/reopens.
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoError, setPhotoError] = useState<string | null>(null);

    // AB#1734 follow-up — schedule state + branches-flash state removed.
    // Schedule editor lives in ScheduleTab now.

    useEffect(() => {
        // AB#1842 (PBI-F) — In Add mode for a restrictive salon, pre-select
        // the only legal gender so the owner sees the form already pointing
        // at the compatible chip. The other chip stays disabled (see render
        // below). Edit mode preserves whatever the existing staff row has —
        // surfacing a mismatch (legacy data) is correct behavior; the
        // Super Admin override path is the only legal way to resolve it.
        const seeded = initial ?? DEFAULT_FORM;
        const seededWithRequiredGender =
            isAdd && requiredGender != null && seeded.gender == null
                ? { ...seeded, gender: requiredGender }
                : seeded;
        setForm(seededWithRequiredGender);
        setSelectedServiceIds(new Set(initialServiceIds));
        setSelectedBranchIds(new Set(initialBranchIds));
        setServiceQuery('');
        setErrors({});
        setPhotoPreview(null);
        setPhotoError(null);

        // CR-2026-05-17 — seed expanded groups from the initial selection
        // (AC-2): on open, a group is expanded if any of its services is
        // already selected so the owner can see what's picked at a glance.
        // Groups with no pre-selection start collapsed (AC-2).
        const selectedSet = new Set(initialServiceIds);
        const expanded = new Set<string>();
        for (const svc of services) {
            if (!selectedSet.has(svc.id)) continue;
            expanded.add(svc.categoryName?.trim() || OTHER_GROUP_KEY);
        }
        setExpandedGroups(expanded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial, isOpen]);

    // AB#1734 follow-up — schedule effects/handlers/helpers moved to ScheduleTab.

    const updateField = <K extends keyof StaffFormData>(key: K, value: StaffFormData[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => {
            const { [key]: _omit, ...rest } = prev;
            return rest;
        });
    };

    const toggleService = (id: number) => {
        if (loading) return;
        setSelectedServiceIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleBranch = (id: number) => {
        if (loading) return;
        setSelectedBranchIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // AB#1734 follow-up — services are owned by a single branch. The staff
    // capability picker MUST scope to the branches the staff is actually
    // enrolled in, otherwise the owner can assign a staff a service at a
    // branch they don't even work at.
    //
    // Rules:
    //   - Single-branch business: no scoping needed; show every service.
    //   - Multi-branch + 0 branches picked (implicit-all branches): show
    //     every service so the modal mirrors the staff-list card's
    //     "Any active service" affordance. Owners reported the previous
    //     "pick a branch first" gate as a dead-end — the staff is in
    //     implicit-all-branches mode (works at every branch), so the
    //     service list should also default to the full set. They can still
    //     narrow by picking branches OR services explicitly.
    //   - Multi-branch + branches picked: filter to services whose branchId
    //     falls within the selected set.
    const isMultiBranch = branches.length >= 2;
    // No longer requires explicit branch pick — implicit-all covers it.
    // Kept as a derived flag (always false now) so the empty-state branch
    // below remains a defensive guard but never trips on normal usage.
    const requiresBranchPick = false;
    const branchScopedServices = useMemo(() => {
        if (!isMultiBranch) return services;
        // Implicit-all branches → every service is in play (mirrors the
        // staff card "Any active service" affordance).
        if (selectedBranchIds.size === 0) return services;
        return services.filter(
            s => s.branchId != null && selectedBranchIds.has(s.branchId),
        );
    }, [services, selectedBranchIds, isMultiBranch]);

    // Prune selected services whose branch is no longer in the picked set.
    // Without this, deselecting a branch leaves stale capability assignments
    // hidden but still saved to the BE. Only runs in multi-branch mode with
    // at least one branch picked (implicit-all preserves the full set).
    useEffect(() => {
        if (!isMultiBranch || selectedBranchIds.size === 0) return;
        setSelectedServiceIds(prev => {
            const next = new Set<number>();
            Array.from(prev).forEach(id => {
                const svc = services.find(s => s.id === id);
                if (svc?.branchId != null && selectedBranchIds.has(svc.branchId)) {
                    next.add(id);
                }
            });
            return prev.size === next.size ? prev : next;
        });
    }, [selectedBranchIds, services, isMultiBranch]);

    // CR-2026-05-17 — render-time derivation of the grouped + filtered
    // service list. Single pass, recomputed when services / search /
    // selection / expand state change.
    const groupRenderStates = useMemo(() => {
        const q = serviceQuery.trim().toLowerCase();
        const isSearching = q.length > 0;
        const allGroups = groupServicesByCategory(branchScopedServices);

        const states = allGroups.map(g => {
            const visibleServices = isSearching
                ? g.services.filter(svc => serviceMatchesQuery(svc, q))
                : g.services;
            const selectedCountInGroup = g.services.reduce(
                (n, svc) => n + (selectedServiceIds.has(svc.id) ? 1 : 0),
                0,
            );
            // AC-2 default expand state OR AC-4 force-expand on search match.
            const isExpanded = isSearching
                ? visibleServices.length > 0
                : expandedGroups.has(g.key);
            return {
                group: g,
                visibleServices,
                visibleServiceIds: new Set(visibleServices.map(s => s.id)),
                selectedCountInGroup,
                totalInGroup: g.services.length,
                isExpanded,
                // AC-4 hides groups with zero matches while searching.
                isVisible: !isSearching || visibleServices.length > 0,
            };
        });

        return { states, isSearching, hasAnyMatch: states.some(s => s.visibleServices.length > 0) };
    }, [branchScopedServices, serviceQuery, selectedServiceIds, expandedGroups]);

    const toggleGroupExpanded = (groupKey: string) => {
        if (loading) return;
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    /**
     * AC-3 — Select all services in a group. When a search query is active,
     * this only affects the services currently visible in the group (i.e.,
     * the matching subset) to avoid surprising the owner by also selecting
     * services they can't see. AC-14 fires the analytics event.
     */
    const selectAllInGroup = (
        groupKey: string,
        visibleServicesInGroup: readonly StaffServiceOption[],
    ) => {
        if (loading) return;
        if (visibleServicesInGroup.length === 0) return;
        setSelectedServiceIds(prev => {
            const next = new Set(prev);
            for (const svc of visibleServicesInGroup) next.add(svc.id);
            trackEvent('staff_modal.bulk_select_clicked', {
                action: 'select_all',
                category_id: groupKey === OTHER_GROUP_KEY ? null : groupKey,
                services_in_group_count: visibleServicesInGroup.length,
                total_selected_after_action: next.size,
            });
            return next;
        });
    };

    /**
     * AC-3 — Clear all services in a group. Like select-all, restricted to
     * the currently-visible subset when a search query is active.
     */
    const clearGroup = (
        groupKey: string,
        visibleServicesInGroup: readonly StaffServiceOption[],
    ) => {
        if (loading) return;
        if (visibleServicesInGroup.length === 0) return;
        setSelectedServiceIds(prev => {
            const next = new Set(prev);
            for (const svc of visibleServicesInGroup) next.delete(svc.id);
            trackEvent('staff_modal.bulk_select_clicked', {
                action: 'clear',
                category_id: groupKey === OTHER_GROUP_KEY ? null : groupKey,
                services_in_group_count: visibleServicesInGroup.length,
                total_selected_after_action: next.size,
            });
            return next;
        });
    };

    // AB#1734 follow-up — schedule-related derived state + handlers removed.
    // ScheduleTab owns these now.

    const handleSave = () => {
        if (loading) return;
        const fullName = form.fullName.trim();
        const email = form.email.trim();
        const phoneNumber = form.phoneNumber.trim();
        const next: Record<string, string> = {};
        if (!fullName) next.fullName = t('ost.modal.err.fullName');
        if (contactNotRequired) {
            // Batch B (AB#2176) — portal access is optional: no contact details means a
            // bookable staff record without a login. Inviting needs both.
            if (email || phoneNumber) {
                if (!email || !phoneNumber) {
                    next[email ? 'phoneNumber' : 'email'] = t('staff.contact.inviteNeedsBoth');
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    next.email = t('staff.contact.invalidEmail');
                }
            }
        } else {
            if (!email) next.email = t('ost.modal.err.email');
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = t('staff.contact.invalidEmail');
            if (!phoneNumber) next.phoneNumber = t('ost.modal.err.phone');
        }

        // AB#1845 (PBI-E) — gender is now required. Reuses the canonical
        // Zod literal-union from staff.schema.ts so the parse rule is the
        // single source of truth (also exercised in tests). On miss we
        // surface the bilingual `staff.errors.genderRequired` string,
        // matching the BE STAFF_GENDER_REQUIRED message.
        const genderParse = staffGenderSchema.safeParse(form.gender);
        if (!genderParse.success) {
            next.gender = t('staff.errors.genderRequired');
        } else if (forbiddenGender != null && form.gender === forbiddenGender) {
            // AB#1842 (PBI-F) — pre-emptively reject a mismatched gender so
            // the owner doesn't pay a round-trip just to hit
            // STAFF_GENDER_MISMATCHES_SALON. The BE still re-checks (the FE
            // chip is `disabled`, but a determined caller can bypass it via
            // devtools). Localized error string mirrors the BE phrasing.
            next.gender = t('staff.errors.genderMismatchesSalon');
        }

        if (Object.keys(next).length > 0) {
            setErrors(next);
            return;
        }
        // AB#1734 follow-up — final defence: filter selected services by the
        // picked branches. Belt-and-braces against the prune-effect not having
        // run yet between a branch-toggle and a Save click in the same tick.
        const safeServiceIds = isMultiBranch && selectedBranchIds.size > 0
            ? Array.from(selectedServiceIds).filter(id => {
                  const svc = services.find(s => s.id === id);
                  return svc?.branchId != null && selectedBranchIds.has(svc.branchId);
              })
            : Array.from(selectedServiceIds);
        onSave(
            { ...form, fullName, email, phoneNumber },
            safeServiceIds,
            Array.from(selectedBranchIds),
        );
    };

    if (!isOpen) return null;

    // S1 (AB#2204, pilot) — the Staff Manager role is not offered: new team members
    // are always Staff. An existing Staff Manager keeps their stored role (sent back
    // unchanged on save — never downgraded silently). S2 (AB#2205, SG-09) — and is
    // no longer labelled "Staff manager": in the pilot every team member shows as Staff.
    // AB#1845 — gender starts null. We render two chips and treat
    // each as selected only when its literal value matches.
    const isMaleSelected = form.gender === STAFF_GENDER_MALE;
    const isFemaleSelected = form.gender === STAFF_GENDER_FEMALE;
    // AB#1842 (PBI-F) — Chip is disabled (and skippable in the tab order)
    // when the host salon's ServiceType forbids that gender. The currently
    // selected chip is never disabled in edit mode, so legacy mismatched
    // staff stay visible until the owner explicitly fixes the row.
    const isMaleDisabled = forbiddenGender === STAFF_GENDER_MALE && !isMaleSelected;
    const isFemaleDisabled = forbiddenGender === STAFF_GENDER_FEMALE && !isFemaleSelected;
    // Parent-supplied BE error trumps the local pre-submit error so the
    // localized message shows on the right field even when the FE failed
    // to gate (e.g., race or older modal copy). AB#1842 extends this to
    // the new STAFF_GENDER_MISMATCHES_SALON code.
    const externalGenderError: string | null =
        externalError?.field === 'gender'
            ? externalError.code === 'STAFF_GENDER_MISMATCHES_SALON'
                ? t('staff.errors.genderMismatchesSalon')
                : t('staff.errors.genderRequired')
            : null;
    const genderError = externalGenderError ?? errors.gender ?? null;

    return (
        <div className={`${styles.modalBackground} ${styles.open}`}>
            <div className={`${styles.modalContainer} ${styles.open}`}>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContent}>
                            <div className={styles.spinner} />
                            <span>{t('staff.saving')}</span>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={() => !loading && onClose()}
                    aria-label={t('ost.modal.close')}
                    disabled={loading}
                >
                    ×
                </button>

                <div className={styles.headerCloseInputsContainer}>
                    <div>
                        <h2 className={styles.header}>{isAdd ? t('ost.modal.addTitle') : t('ost.modal.editTitle')}</h2>
                        <p className={styles.subheader}>
                            {isAdd
                                ? t('staff.add.subtitleRecord')
                                : t('ost.modal.editSubtitle')}
                        </p>
                    </div>

                    {/* CR-2026-05-16-v1 — Photo widget. Optional for save. Only
                        enabled in edit mode (we need a staffId to upload to —
                        and the staff record doesn't exist until they accept
                        the invitation). For add mode, surface a friendly hint
                        instead of the upload affordance. */}
                    {/* AB#1734 follow-up — Grouped card #1: ABOUT.
                        Wraps identity + contact + role + active. Visual
                        grouping that matches Dina's structural audit. */}
                    <section className={styles.staffCard}>
                        <h3 className={styles.staffCardTitle}>{t('ost.modal.about')}</h3>

                    <div className={styles.photoSection}>
                        <label className={styles.sectionLabel}>{t('ost.modal.photo')}</label>
                        <div className={styles.photoRow}>
                            <div className={styles.photoAvatar} aria-hidden="true">
                                {(photoPreview ?? form.photoUrl) ? (
                                    <img
                                        src={photoPreview ?? resolveStaffPhoto(form.photoUrl)}
                                        alt=""
                                    />
                                ) : (
                                    <span className={styles.photoInitials}>
                                        {getInitials(form.fullName)}
                                    </span>
                                )}
                                {photoUploading && (
                                    <div className={styles.photoSpinner}>
                                        <div className={styles.spinner} />
                                    </div>
                                )}
                            </div>

                            <div className={styles.photoControls}>
                                {isAdd ? (
                                    <p className={styles.photoHint}>
                                        {t('ost.modal.photoAddHint')}
                                    </p>
                                ) : (
                                    <>
                                        <label className={`${styles.btn} ${styles.btnGhost} ${styles.photoBtn}`}>
                                            {form.photoUrl ? t('ost.modal.changePhoto') : t('ost.modal.uploadPhoto')}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/gif"
                                                hidden
                                                disabled={loading || photoUploading || !onUploadPhoto}
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file || !onUploadPhoto) return;
                                                    setPhotoError(null);
                                                    setPhotoUploading(true);
                                                    // Optimistic preview via local object URL.
                                                    const localUrl = URL.createObjectURL(file);
                                                    setPhotoPreview(localUrl);
                                                    try {
                                                        const newUrl = await onUploadPhoto(file);
                                                        setForm(prev => ({ ...prev, photoUrl: newUrl }));
                                                    } catch (err) {
                                                        setPhotoError(err instanceof Error ? err.message : t('ost.modal.photoFailed'));
                                                        setPhotoPreview(null);
                                                    } finally {
                                                        setPhotoUploading(false);
                                                        e.target.value = ''; // allow re-selecting the same file
                                                    }
                                                }}
                                            />
                                        </label>
                                        <p className={styles.photoHint}>
                                            {t('ost.modal.photoHint')}
                                        </p>
                                        {photoError && (
                                            <div className={styles.fieldError}>{photoError}</div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                            <label htmlFor="staff-fullName">{t('ost.modal.fullName')} *</label>
                            <input
                                id="staff-fullName"
                                type="text"
                                value={form.fullName}
                                onChange={e => updateField('fullName', e.target.value)}
                                placeholder={t('ost.modal.fullNamePlaceholder')}
                                disabled={loading}
                                autoComplete="off"
                            />
                            {errors.fullName && <div className={styles.fieldError}>{errors.fullName}</div>}
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label htmlFor="staff-email">{contactNotRequired ? t('staff.contact.emailOptional') : `${t('ost.modal.email')} *`}</label>
                            <input
                                id="staff-email"
                                type="email"
                                value={form.email}
                                onChange={e => updateField('email', e.target.value)}
                                placeholder="name@example.com"
                                disabled={loading || !contactOptional /* email is identity once invited */}
                                autoComplete="off"
                            />
                            {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
                        </div>

                        <div className={styles.field}>
                            <label htmlFor="staff-phone">{contactNotRequired ? t('staff.contact.phoneOptional') : `${t('ost.modal.phone')} *`}</label>
                            <input
                                id="staff-phone"
                                type="tel"
                                value={form.phoneNumber}
                                onChange={e => updateField('phoneNumber', e.target.value)}
                                placeholder="+971 …"
                                disabled={loading || (!contactOptional && !initial?.hasLogin)}
                                autoComplete="off"
                            />
                            {errors.phoneNumber && <div className={styles.fieldError}>{errors.phoneNumber}</div>}
                        </div>
                    </div>
                    {!isAdd && !initial?.hasLogin && (
                        <p className={styles.subheader} role="note">
                            {initial?.invitationPending ? t('staff.status.invitePending') : t('staff.edit.inviteHint')}
                        </p>
                    )}

                    <div className={styles.row}>
                        <div className={styles.field}>
                            {/* AB#1845 (PBI-E) — Gender REQUIRED. Asterisk
                                + binary chip pair (no default selection).
                                AB#1842 (PBI-F) — Restrictive salons (MenOnly
                                / WomenOnly) grey out the incompatible chip,
                                expose `aria-describedby` pointing at the
                                constraint tooltip, and pre-select the only
                                legal gender in Add mode. */}
                            <label id="staff-gender-label" htmlFor="staff-gender-male">
                                {t('staff.fields.gender')} *
                            </label>
                            <div
                                className={styles.capabilityChips}
                                role="radiogroup"
                                aria-labelledby="staff-gender-label"
                                aria-required="true"
                                aria-invalid={!!genderError}
                                aria-describedby={
                                    [
                                        genderError ? 'staff-gender-error' : null,
                                        genderTooltip ? 'staff-gender-constraint' : null,
                                    ].filter(Boolean).join(' ') || undefined
                                }
                            >
                                <button
                                    id="staff-gender-male"
                                    type="button"
                                    role="radio"
                                    aria-checked={isMaleSelected}
                                    aria-disabled={isMaleDisabled || undefined}
                                    title={isMaleDisabled ? genderTooltip ?? undefined : undefined}
                                    className={`${styles.capabilityChip} ${isMaleSelected ? styles.capabilityChipOn : ''} ${isMaleDisabled ? styles.capabilityChipInactive : ''}`}
                                    onClick={() => !loading && !isMaleDisabled && updateField('gender', STAFF_GENDER_MALE)}
                                    disabled={loading || isMaleDisabled}
                                >
                                    {isMaleSelected && (
                                        <span aria-hidden="true" className={styles.capabilityChipCheck}>✓</span>
                                    )}
                                    <span>{t('staff.fields.gender.option.male')}</span>
                                </button>
                                <button
                                    id="staff-gender-female"
                                    type="button"
                                    role="radio"
                                    aria-checked={isFemaleSelected}
                                    aria-disabled={isFemaleDisabled || undefined}
                                    title={isFemaleDisabled ? genderTooltip ?? undefined : undefined}
                                    className={`${styles.capabilityChip} ${isFemaleSelected ? styles.capabilityChipOn : ''} ${isFemaleDisabled ? styles.capabilityChipInactive : ''}`}
                                    onClick={() => !loading && !isFemaleDisabled && updateField('gender', STAFF_GENDER_FEMALE)}
                                    disabled={loading || isFemaleDisabled}
                                >
                                    {isFemaleSelected && (
                                        <span aria-hidden="true" className={styles.capabilityChipCheck}>✓</span>
                                    )}
                                    <span>{t('staff.fields.gender.option.female')}</span>
                                </button>
                            </div>
                            {genderTooltip && (
                                <div
                                    id="staff-gender-constraint"
                                    className={styles.fieldHint}
                                    role="note"
                                >
                                    {genderTooltip}
                                </div>
                            )}
                            {genderError && (
                                <div id="staff-gender-error" className={styles.fieldError} role="alert">
                                    {genderError}
                                </div>
                            )}
                        </div>

                        <div className={styles.field}>
                            <label htmlFor="staff-position">{t('ost.modal.position')}</label>
                            <input
                                id="staff-position"
                                type="text"
                                value={form.position}
                                onChange={e => updateField('position', e.target.value)}
                                placeholder={t('ost.modal.positionPlaceholder')}
                                disabled={loading}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className={`${styles.toggleRow} ${styles.staffCardActiveDivider}`}>
                        <div>
                            <div className={styles.toggleLabel}>{t('staff.status.active')}</div>
                            <div className={styles.toggleHint}>
                                {t('ost.modal.activeHint')}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            data-on={form.isActive ? 'true' : 'false'}
                            onClick={() => !loading && updateField('isActive', !form.isActive)}
                            aria-pressed={form.isActive}
                            aria-label={t('staff.status.active')}
                            disabled={loading}
                        />
                    </div>

                    </section>
                    {/* AB#1734 follow-up — Grouped card #2: WHAT THEY DO.
                        Wraps capabilities — branches (when multi-branch) + services. */}
                    <section className={styles.staffCard}>
                        <h3 className={styles.staffCardTitle}>{t('ost.modal.whatTheyDo')}</h3>

                    {/* CR-2026-05-16-v2 — Works at branches.
                        Mirrors the capability chip pattern. Only renders when
                        the business has ≥2 branches (single-branch salons have
                        nothing to pick from). Implicit-all default: empty
                        selection = eligible at any active branch. */}
                    {branches.length >= 2 && (
                        <div className={styles.capabilitySection}>
                            <label className={styles.sectionLabel}>{t('ost.modal.worksAt')}</label>
                            <p className={styles.capabilityHint}>
                                {selectedBranchIds.size === 0
                                    ? t('ost.modal.branchesNone')
                                    : fill(t(selectedBranchIds.size === 1 ? 'ost.modal.branchesPickedOne' : 'ost.modal.branchesPicked'), { count: selectedBranchIds.size })}
                            </p>
                            <div className={styles.capabilityChips}>
                                {branches.map(b => {
                                    const isSelected = selectedBranchIds.has(b.id);
                                    return (
                                        <button
                                            key={b.id}
                                            type="button"
                                            className={`${styles.capabilityChip} ${isSelected ? styles.capabilityChipOn : ''}`}
                                            onClick={() => toggleBranch(b.id)}
                                            disabled={loading}
                                            aria-pressed={isSelected}
                                        >
                                            {isSelected && (
                                                <span aria-hidden="true" className={styles.capabilityChipCheck}>✓</span>
                                            )}
                                            <span>{b.name}</span>
                                            {b.isPrimary && (
                                                <span className={styles.capabilityChipBadge}>{t('ost.modal.hq')}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Capability set */}
                    <div className={styles.capabilitySection}>
                        <label className={styles.sectionLabel}>{t('ost.modal.services')}</label>
                        <p className={styles.capabilityHint}>
                            {selectedServiceIds.size === 0
                                ? (isMultiBranch
                                    ? (selectedBranchIds.size === 0
                                        ? t('ost.modal.servicesNoneAllBranches')
                                        : t('ost.modal.servicesNonePickedBranches'))
                                    : t('ost.modal.servicesNone'))
                                : fill(t(selectedServiceIds.size === 1 ? 'ost.modal.servicesPickedOne' : 'ost.modal.servicesPicked'), { count: selectedServiceIds.size })}
                        </p>

                        {services.length === 0 ? (
                            <div className={styles.capabilityEmpty}>
                                {t('ost.modal.noServices')}
                            </div>
                        ) : requiresBranchPick ? (
                            <div className={styles.capabilityEmpty}>
                                {t('ost.modal.pickBranchFirst')}
                            </div>
                        ) : branchScopedServices.length === 0 ? (
                            <div className={styles.capabilityEmpty}>
                                {t('ost.modal.noServicesAtBranches')}
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    className={styles.capabilitySearch}
                                    value={serviceQuery}
                                    onChange={e => setServiceQuery(e.target.value)}
                                    placeholder={fill(t('ost.modal.searchServices'), { count: branchScopedServices.length })}
                                    disabled={loading}
                                    aria-label={t('ost.modal.searchServicesAria')}
                                />
                                {/* AC-1 / AC-2 / AC-3 / AC-4 — collapsible category groups with bulk actions.
                                    Each group: header (chevron + label + counts + bulk buttons) → group body
                                    rendered only when expanded. While searching, groups with zero matches hide
                                    (AC-4); matching groups auto-expand and render only matching services. */}
                                {groupRenderStates.isSearching && !groupRenderStates.hasAnyMatch ? (
                                    <div className={styles.capabilityNoMatchRow}>
                                        <span className={styles.capabilityNoMatch}>
                                            {fill(t('ost.modal.noMatch'), { query: serviceQuery })}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.capabilityClearSearch}
                                            onClick={() => setServiceQuery('')}
                                            disabled={loading}
                                        >
                                            {t('ost.modal.clearSearch')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.capabilityGroups}>
                                        {groupRenderStates.states.map(state => {
                                            if (!state.isVisible) return null;
                                            const { group, visibleServices: visibleInGroup, selectedCountInGroup, totalInGroup, isExpanded } = state;
                                            const visibleSelectedCount = visibleInGroup.reduce(
                                                (n, svc) => n + (selectedServiceIds.has(svc.id) ? 1 : 0),
                                                0,
                                            );
                                            const allVisibleSelected = visibleInGroup.length > 0 && visibleSelectedCount === visibleInGroup.length;
                                            const noneVisibleSelected = visibleSelectedCount === 0;
                                            const groupBodyId = `staff-svc-group-${group.key.replace(/\s+/g, '-')}`;
                                            const groupLabel = group.key === OTHER_GROUP_KEY ? t('ost.modal.otherCategory') : group.label;
                                            return (
                                                <div key={group.key} className={styles.capabilityGroup}>
                                                    <div className={styles.capabilityGroupHeader}>
                                                        <button
                                                            type="button"
                                                            className={styles.capabilityGroupToggle}
                                                            onClick={() => toggleGroupExpanded(group.key)}
                                                            aria-expanded={isExpanded}
                                                            aria-controls={groupBodyId}
                                                            disabled={loading}
                                                        >
                                                            <span
                                                                aria-hidden="true"
                                                                className={`${styles.capabilityGroupChevron} ${isExpanded ? styles.capabilityGroupChevronOpen : ''}`}
                                                            >
                                                                ▸
                                                            </span>
                                                            <span className={styles.capabilityGroupLabel}>{groupLabel}</span>
                                                            <span className={styles.capabilityGroupCount}>
                                                                {fill(t(totalInGroup === 1 ? 'ost.capability.countOne' : 'ost.capability.count'), { count: totalInGroup })}
                                                                {selectedCountInGroup > 0 && (
                                                                    <>
                                                                        {' • '}
                                                                        <span className={styles.capabilityGroupCountSelected}>
                                                                            {fill(t('ost.modal.selectedCount'), { count: selectedCountInGroup })}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </span>
                                                        </button>
                                                        <div className={styles.capabilityGroupActions}>
                                                            <button
                                                                type="button"
                                                                className={styles.capabilityGroupAction}
                                                                onClick={() => selectAllInGroup(group.key, visibleInGroup)}
                                                                disabled={loading || allVisibleSelected || visibleInGroup.length === 0}
                                                                aria-label={fill(t('ost.modal.selectAllAria'), { count: visibleInGroup.length, group: groupLabel })}
                                                            >
                                                                {t('ost.modal.selectAll')}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={styles.capabilityGroupAction}
                                                                onClick={() => clearGroup(group.key, visibleInGroup)}
                                                                disabled={loading || noneVisibleSelected || visibleInGroup.length === 0}
                                                                aria-label={fill(t('ost.modal.clearGroupAria'), { group: groupLabel })}
                                                            >
                                                                {t('ost.modal.clear')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div
                                                            id={groupBodyId}
                                                            className={`${styles.capabilityChips} ${styles.capabilityGroupBody}`}
                                                        >
                                                            {visibleInGroup.map(svc => {
                                                                const isSelected = selectedServiceIds.has(svc.id);
                                                                const isInactive = svc.isActive === false;
                                                                return (
                                                                    <button
                                                                        key={svc.id}
                                                                        type="button"
                                                                        className={`${styles.capabilityChip} ${isSelected ? styles.capabilityChipOn : ''} ${isInactive ? styles.capabilityChipInactive : ''}`}
                                                                        onClick={() => toggleService(svc.id)}
                                                                        disabled={loading}
                                                                        aria-pressed={isSelected}
                                                                    >
                                                                        {isSelected && (
                                                                            <span aria-hidden="true" className={styles.capabilityChipCheck}>✓</span>
                                                                        )}
                                                                        <span>{svc.name}</span>
                                                                        {isInactive && (
                                                                            <span className={styles.capabilityChipBadge}>{t('ost.modal.inactiveBadge')}</span>
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    </section>
                    {/* End WHAT THEY DO card */}

                    {/* AB#1976 (CR-2026-07-04) — Staff absence management.
                        Edit mode only (needs a persisted staffId). */}
                    {!isAdd && form.id != null && (
                        <section className={styles.staffCard}>
                            <StaffAbsencesSection staffId={form.id} branches={branches} />
                        </section>
                    )}

                </div>

                <div className={styles.buttonContainer}>
                    <div className={styles.deleteSlot}>
                        {!isAdd && onDelete && (
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={onDelete}
                                disabled={loading}
                            >
                                {t('ost.modal.delete')}
                            </button>
                        )}
                    </div>
                    <div className={styles.cancelAddSave}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={() => !loading && onClose()}
                            disabled={loading}
                        >
                            {t('staff.modal.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading
                                ? t('staff.saving')
                                : (isAdd
                                    ? (form.email.trim() ? t('staff.add.submitAndInvite') : t('staff.add.submit'))
                                    : t('staff.modal.save'))}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageStaffModal;
